import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dlCardImage from "../images/DL_Card.png";
import SearchUtils from "../utils/SearchUtils";

const ScanIdDialog = ({
  open,
  onClose,
  onSubmit,
  t,
  isSubmitting = false,
  error = "",
  success = false,
  enableTrim = false,
  trimChars = SearchUtils.defaultTrimChars,
}) => {
  const [scannedValue, setScannedValue] = useState("");
  const [imageError, setImageError] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const hiddenInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const scanBufferRef = useRef("");

  const SCAN_DEBOUNCE_MS = 150;

  useEffect(() => {
    if (open) {
      setScannedValue("");
      setImageError(false);
      setIsScanning(false);
      scanBufferRef.current = "";
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setTimeout(() => {
        if (hiddenInputRef.current) {
          hiddenInputRef.current.focus();
        }
      }, 150);
    } else {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (error) {
      setScannedValue("");
      setIsScanning(false);
      scanBufferRef.current = "";
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = "";
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setTimeout(() => {
        if (hiddenInputRef.current) {
          hiddenInputRef.current.focus();
        }
      }, 100);
    }
  }, [error]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    if (!isSubmitting) {
      setScannedValue("");
      setIsScanning(false);
      scanBufferRef.current = "";
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      onClose && onClose();
    }
  };

  const handleHiddenInputChange = (event) => {
    const value = event.target.value || "";
    const previousBuffer = scanBufferRef.current;

    if (value.length >= previousBuffer.length) {
      scanBufferRef.current = value;
      
      if (value.length > 0 && previousBuffer.length === 0) {
        setIsScanning(true);
        setScannedValue("");
      } else if (value.length > 0) {
        setIsScanning(true);
        setScannedValue("");
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      debounceTimerRef.current = setTimeout(() => {
        const raw = scanBufferRef.current.trim();
        const finalValue = enableTrim
          ? SearchUtils.trimLeadingTrailing(raw, trimChars)
          : raw;
        
        setScannedValue(finalValue);
        setIsScanning(false);
        
        if (hiddenInputRef.current) {
          hiddenInputRef.current.value = "";
        }
        scanBufferRef.current = "";
        
        if (finalValue.length > 0 && !isSubmitting && onSubmit) {
          onSubmit(finalValue);
        }
      }, SCAN_DEBOUNCE_MS);
    } else {
      setIsScanning(false);
      setScannedValue("");
      scanBufferRef.current = "";
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  };


  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: "1.15rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {t("Console.ActionPopup.ScanIdTitle", {
          defaultValue: "Scan ID",
        })}
        <IconButton
          aria-label={t("Global.Close") || "Close"}
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.18)",
            },
            "&:disabled": {
              color: "rgba(255, 255, 255, 0.5)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          backgroundColor: "#ffffff",
        }}
      >
        {success ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              gap: 2,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: "#22c55e",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#166534",
                textAlign: "center",
              }}
            >
              {t("Console.ActionPopup.ScanIdSuccess", {
                defaultValue: "ID scanned successfully!",
              })}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                textAlign: "center",
              }}
            >
              {t("Console.ActionPopup.ScanIdSuccessMessage", {
                defaultValue: "The ID barcode has been saved.",
              })}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
                p: 2,
                backgroundColor: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                minHeight: "200px",
              }}
            >
              {imageError ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    py: 3,
                  }}
                >
                  <QrCodeScannerIcon
                    sx={{
                      fontSize: 64,
                      color: "#7c3aed",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      textAlign: "center",
                      fontWeight: 500,
                    }}
                  >
                    {t("Console.ActionPopup.ScanIdImageFallback", {
                      defaultValue: "Scan the barcode on the back of the ID",
                    })}
                  </Typography>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={dlCardImage}
                  alt="Driver's License - Barcode Location"
                  onError={() => setImageError(true)}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                mb: 2,
                textAlign: "center",
              }}
            >
              {t("Console.ActionPopup.ScanIdInstruction", {
                defaultValue: "Scan the ID barcode using your scanner",
              })}
            </Typography>
            <Box sx={{ position: "relative" }}>
              <input
                ref={hiddenInputRef}
                type="text"
                maxLength={1000}
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
                onChange={handleHiddenInputChange}
                autoFocus
              />
              <TextField
                fullWidth
                variant="outlined"
                placeholder={
                  isScanning
                    ? t("Console.ActionPopup.Scanning", {
                        defaultValue: "Scanning...",
                      })
                    : t("Console.ActionPopup.ScanIdPlaceholder", {
                        defaultValue: "Scan barcode using your scanner...",
                      })
                }
                value={scannedValue}
                onFocus={() => {
                  if (hiddenInputRef.current) {
                    hiddenInputRef.current.focus();
                  }
                }}
                inputProps={{
                  maxLength: 1000,
                  readOnly: true,
                }}
                disabled={isSubmitting}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        mr: 1.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <QrCodeScannerIcon
                        sx={{
                          color: isSubmitting ? "#b0bec5" : "#7c3aed",
                          fontSize: 24,
                        }}
                      />
                    </Box>
                  ),
                  endAdornment: isScanning && !isSubmitting ? (
                    <Box
                      sx={{
                        ml: 1.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={20} sx={{ color: "#7c3aed" }} />
                    </Box>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    transition: "all 0.3s ease",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isScanning ? "#7c3aed" : "#e2e8f0",
                      borderWidth: 2,
                    },
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#7c3aed",
                      },
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#7c3aed",
                        borderWidth: 2,
                      },
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#f1f5f9",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#cbd5e1",
                      },
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "0.875rem",
                    color: "#9e9e9e",
                    opacity: 1,
                  },
                }}
              />
            </Box>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                }}
                onClose={() => {}}
              >
                {error || t("Console.ActionPopup.InvalidId", {
                  defaultValue: "Invalid ID",
                })}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
    );
};

ScanIdDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  error: PropTypes.string,
  success: PropTypes.bool,
  enableTrim: PropTypes.bool,
  trimChars: PropTypes.string,
};

ScanIdDialog.defaultProps = {
  isSubmitting: false,
  error: "",
  success: false,
  enableTrim: false,
  trimChars: SearchUtils.defaultTrimChars,
};

export default ScanIdDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/ScanIdDialog.jsx